import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AggregatedIngredient, SendToBringResponse } from '@ricettario/shared';
import { api, ApiError } from '../api/client';
import { useMenu } from '../hooks/useMenus';
import { formatQuantity } from '../lib/scale';

export default function MenuSummaryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: menu, loading, error } = useMenu(id);

  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendToBringResponse | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    // reset esclusioni ad ogni nuova apertura del menu
    setExcluded(new Set());
  }, [menu?.id]);

  const aggregated: AggregatedIngredient[] = useMemo(() => menu?.aggregated ?? [], [menu]);
  const selectedCount = aggregated.length - excluded.size;

  function toggle(key: string) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function send() {
    if (!menu || sending || selectedCount === 0) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await api.post<SendToBringResponse>(
        `/api/v1/menus/${menu.id}/send-to-bring`,
        { excludedKeys: Array.from(excluded) },
      );
      setResult(res);
    } catch (err) {
      setSendError(err instanceof ApiError ? err.message : 'errore invio');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <main className="max-w-xl mx-auto px-5 py-6 space-y-3">
        <div className="skeleton h-5 w-20" />
        <div className="skeleton h-8 w-1/2" />
        <div className="skeleton h-40 w-full mt-6" />
      </main>
    );
  }
  if (error || !menu) {
    return (
      <main className="max-w-xl mx-auto px-5 py-20 text-center space-y-5">
        <p className="text-brand text-lg font-bold">{error ?? 'Menu non trovato.'}</p>
        <Link to="/menus" className="btn-soft">← torna</Link>
      </main>
    );
  }

  if (result) {
    return (
      <main className="max-w-xl mx-auto px-5 py-16 text-center space-y-6 animate-riseIn">
        <div className="mx-auto h-20 w-20 rounded-full bg-sageSoft flex items-center justify-center text-5xl animate-pop">
          ✅
        </div>
        <h1 className="text-3xl font-extrabold tracking-tightest">Spesa aggiunta!</h1>
        <p className="text-muted">
          {result.sent.length} vo{result.sent.length === 1 ? 'ce' : 'ci'} aggiunte a Bring per{' '}
          <strong className="text-ink">{menu.items.length}</strong> ricett
          {menu.items.length === 1 ? 'a' : 'e'}.
        </p>
        {result.failed.length > 0 && (
          <div className="card p-5 text-left">
            <p className="text-sm font-bold text-brand mb-2">
              {result.failed.length} non invi{result.failed.length === 1 ? 'ata' : 'ate'}
            </p>
            <ul className="space-y-1.5 text-sm text-muted">
              {result.failed.map((f) => (
                <li key={f.name}>
                  <strong className="text-ink">{f.name}</strong> — {f.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex gap-2 justify-center">
          <button className="btn-soft" onClick={() => navigate(`/menu/${menu.id}`)}>
            torna al menu
          </button>
          <button className="btn-brand" onClick={() => navigate('/menus')}>
            tutti i menu
          </button>
        </div>
      </main>
    );
  }

  if (aggregated.length === 0) {
    return (
      <main className="max-w-xl mx-auto px-5 py-20 text-center space-y-5">
        <p className="text-muted">Nessun ingrediente nel menu.</p>
        <Link to={`/menu/${menu.id}`} className="btn-soft">← torna al menu</Link>
      </main>
    );
  }

  return (
    <main className="max-w-xl mx-auto px-5 pt-5 pb-32">
      <nav className="mb-5">
        <Link to={`/menu/${menu.id}`} className="btn-ghost">← menu</Link>
      </nav>

      <header className="mb-6 animate-riseIn">
        <h1 className="text-[28px] font-extrabold tracking-tightest leading-[1.05]">
          Spesa della <span className="text-brand">settimana</span>
        </h1>
        <p className="text-sm text-muted mt-2 leading-relaxed">
          {aggregated.length} ingredient{aggregated.length === 1 ? 'e' : 'i'} per{' '}
          <strong className="text-ink">
            {menu.items.length} ricett{menu.items.length === 1 ? 'a' : 'e'}
          </strong>
          . Togli quelli che hai già in dispensa.
        </p>
      </header>

      <ul className="card divide-y divide-line mb-4 animate-riseIn">
        {aggregated.map((ing) => {
          const checked = !excluded.has(ing.key);
          const isMerged = ing.sources.length > 1;
          return (
            <li key={ing.key}>
              <label
                htmlFor={`ing-${ing.key}`}
                className="flex items-start gap-4 px-5 py-3.5 cursor-pointer"
              >
                <span
                  className={`h-6 w-6 rounded-full border-2 transition flex items-center justify-center shrink-0 mt-0.5 ${
                    checked ? 'bg-brand border-brand text-white' : 'border-line'
                  }`}
                  aria-hidden
                >
                  {checked && (
                    <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 6l3 3 5-6" />
                    </svg>
                  )}
                </span>
                <input
                  id={`ing-${ing.key}`}
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(ing.key)}
                  className="sr-only"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">{ing.name}</span>
                    <span className="text-muted font-mono text-sm tabular-nums shrink-0">
                      {formatQuantity(ing.totalQuantity)}
                      {ing.unit ? ` ${ing.unit}` : ''}
                    </span>
                  </div>
                  {isMerged && (
                    <p className="text-xs text-muted mt-1">
                      da {ing.sources.length} ricette:{' '}
                      {ing.sources.map((s) => s.recipeName).join(', ')}
                    </p>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-2 mb-6">
        <button className="btn-ghost text-sm" onClick={() => setExcluded(new Set())}>
          tutti
        </button>
        <span className="text-muted">·</span>
        <button
          className="btn-ghost text-sm"
          onClick={() => setExcluded(new Set(aggregated.map((a) => a.key)))}
        >
          nessuno
        </button>
      </div>

      {sendError && <p className="text-brand font-medium mb-4">{sendError}</p>}

      <div className="bottom-bar">
        <div className="max-w-xl mx-auto px-5">
          <button
            className="btn-brand w-full py-4"
            onClick={send}
            disabled={sending || selectedCount === 0}
          >
            {sending ? 'invio…' : `Invia ${selectedCount} alla spesa`}
          </button>
        </div>
      </div>
    </main>
  );
}
